import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {
  createUser,
  updateUser,
  getDepartments,
  getJobTitles,
  getBranches,
  uploadUserPhoto,
  uploadUserSignature,
  getUserPhotoUrl,
  getUserSignatureUrl,
} from '../api/userApi';
import { getRoleOptions } from '../api/roleApi';
import useModalEffect from '../../../hooks/useModalEffect';
import { AppSelect } from '../../../components/common/select';

const STATUS_OPTIONS = [
  { id: '1', name: 'Active' },
  { id: '0', name: 'Inactive' },
];

const EMPTY_FORM = {
  username: '',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  employeeId: '',
  departementId: '',
  jobTitleId: '',
  branchIds: [],
  roles: [],
  nohp: '',
  address: '',
  emergencyContact: '',
  emergencyAddress: '',
  businessLocation: '',
  country: '',
  state: '',
  city: '',
  postalCode: '',
  status: '1',
};

const CreateUserForm = ({ isOpen, onClose, onSubmit, initialData, isEditMode = false }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [profilePicture, setProfilePicture] = useState(null);
  const [signature, setSignature] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        username: initialData.username || '',
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        email: initialData.email || '',
        password: '', // never prefill password
        employeeId: initialData.employeeId || '',
        departementId: String(initialData.departementId || initialData.departmentId || ''),
        jobTitleId: String(initialData.jobTitleId || ''),
        branchIds: Array.isArray(initialData.branchIds) ? initialData.branchIds.map(String) : [],
        roles: initialData.roles ? initialData.roles.map(r => r.name || r) : [],
        nohp: initialData.nohp || '',
        address: initialData.address || '',
        emergencyContact: initialData.emergencyContact || '',
        emergencyAddress: initialData.emergencyAddress || '',
        businessLocation: initialData.businessLocation || '',
        country: initialData.country || '',
        state: initialData.state || '',
        city: initialData.city || '',
        postalCode: initialData.postalCode || '',
        status: initialData.status || '1',
      });
      setProfilePreview(initialData.photo ? getUserPhotoUrl(initialData.photo) : null);
      setSignaturePreview(initialData.signature ? getUserSignatureUrl(initialData.signature) : null);
    } else {
      setFormData(EMPTY_FORM);
      setProfilePreview(null);
      setSignaturePreview(null);
    }
    setProfilePicture(null);
    setSignature(null);
  }, [initialData, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setIsLoadingRoles(true);
      try {
        const [options, deptList, jobList, branchList] = await Promise.all([
          getRoleOptions(),
          getDepartments().catch(() => []),
          getJobTitles().catch(() => []),
          getBranches().catch(() => []),
        ]);
        if (!cancelled) {
          setRoleOptions(options);
          setDepartments(deptList || []);
          setJobTitles(jobList || []);
          setBranches(branchList || []);
        }
      } finally {
        if (!cancelled) setIsLoadingRoles(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setProfilePicture(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setProfilePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setSignature(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setSignaturePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!isEditMode && !formData.password.trim()) errors.password = 'Password is required';
    if (!formData.employeeId.trim()) errors.employeeId = 'Employee ID is required';
    if (!formData.departementId) errors.departementId = 'Department is required';
    if (!formData.jobTitleId) errors.jobTitleId = 'Job title is required';
    if (!formData.nohp.trim()) errors.nohp = 'No HP is required';
    else if (formData.nohp.trim().length > 15) errors.nohp = 'No HP must be 15 characters or fewer';
    if (!formData.roles.length) errors.roles = 'At least one role is required';
    return errors;
  };

  const resetAndClose = () => {
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setProfilePicture(null);
    setSignature(null);
    setProfilePreview(null);
    setSignaturePreview(null);
    onClose();
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {
      let result;

      if (isEditMode && initialData?.id) {
        result = await updateUser(initialData.id, formData);
      } else {
        result = await createUser(formData);
      }

      const savedUserId = initialData?.id || result?.id;
      if (savedUserId) {
        if (profilePicture) {
          try { await uploadUserPhoto(savedUserId, profilePicture); } catch (err) { console.warn('Could not upload photo:', err); }
        }
        if (signature) {
          try { await uploadUserSignature(savedUserId, signature); } catch (err) { console.warn('Could not upload signature:', err); }
        }
      }

      onSubmit(result);
      resetAndClose();
    } catch (error) {
      console.error('Error saving user:', error);

      if (error.response?.data) {
        const errorMessage = error.response.data;
        if (typeof errorMessage === 'string') {
          if (errorMessage.includes('Username already taken')) {
            setFormErrors({ username: 'Username already taken' });
          } else if (errorMessage.includes('Email already taken')) {
            setFormErrors({ email: 'Email already taken' });
          } else {
            setFormErrors({ general: errorMessage });
          }
        } else {
          setFormErrors({ general: 'An error occurred while saving the user' });
        }
      } else {
        setFormErrors({ general: 'An error occurred while saving the user' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    resetAndClose();
  };

  return (
    <>
      <style>{`
        #modal-create-user .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
          margin-top: 1.25rem;
        }
        #modal-create-user .inv-section-label:first-child { margin-top: 0; }
        #modal-create-user .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-create-user .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-create-user .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-create-user .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-create-user .inv-required-star { color: #e53935; margin-left: 2px; }
        #modal-create-user .modal-body { max-height: 70vh; overflow-y: auto; }
        #modal-create-user .avatar-upload-preview {
          width: 64px; height: 64px; border-radius: 50%; object-fit: cover;
          background: #f1f3f5; border: 1px solid #e9ecef;
        }
      `}</style>
      <div id="modal-create-user" className={`modal modal-blur fade ${isOpen ? 'show' : ''}`}
           style={{ display: isOpen ? 'block' : 'none' }}
           tabIndex="-1"
           role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{isEditMode ? 'Edit User' : 'Create User'}</h5>
              <button type="button" className="btn-close" onClick={handleModalClose}></button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                {formErrors.general && (
                  <div className="alert alert-danger" role="alert">
                    {formErrors.general}
                  </div>
                )}

                <div className="inv-section-label">Account</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Username <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.username ? 'is-invalid' : ''}`}
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="Enter username"
                      disabled={isSubmitting}
                    />
                    {formErrors.username && <div className="invalid-feedback">{formErrors.username}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email <span className="text-danger">*</span></label>
                    <input
                      type="email"
                      className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email"
                      disabled={isSubmitting}
                    />
                    {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">First Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.firstName ? 'is-invalid' : ''}`}
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter first name"
                      disabled={isSubmitting}
                    />
                    {formErrors.firstName && <div className="invalid-feedback">{formErrors.firstName}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Last Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.lastName ? 'is-invalid' : ''}`}
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Enter last name"
                      disabled={isSubmitting}
                    />
                    {formErrors.lastName && <div className="invalid-feedback">{formErrors.lastName}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Password {!isEditMode && <span className="text-danger">*</span>}
                    </label>
                    <input
                      type="password"
                      className={`form-control ${formErrors.password ? 'is-invalid' : ''}`}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder={isEditMode ? 'Leave blank to keep current password' : 'Enter password'}
                      disabled={isSubmitting}
                    />
                    {formErrors.password && <div className="invalid-feedback">{formErrors.password}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">No HP <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.nohp ? 'is-invalid' : ''}`}
                      name="nohp"
                      value={formData.nohp}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                      maxLength={15}
                      disabled={isSubmitting}
                    />
                    {formErrors.nohp && <div className="invalid-feedback">{formErrors.nohp}</div>}
                  </div>
                  <div className="col-md-6">
                    <AppSelect
                      name="status"
                      label="Status"
                      value={formData.status}
                      onChange={handleSelectChange}
                      options={STATUS_OPTIONS}
                      valueField="id"
                      labelField="name"
                      searchField="name"
                      disabled={isSubmitting}
                      placeholder="Select status"
                    />
                  </div>
                  <div className="col-md-6">
                    <AppSelect
                      name="roles"
                      label="Roles"
                      required
                      value={formData.roles}
                      onChange={handleSelectChange}
                      options={roleOptions}
                      valueField="value"
                      labelField="label"
                      searchField="label"
                      multiple
                      disabled={isSubmitting || isLoadingRoles}
                      error={formErrors.roles}
                      placeholder="Select roles"
                    />
                  </div>
                </div>

                <div className="inv-section-label">Employment</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Employee ID <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.employeeId ? 'is-invalid' : ''}`}
                      name="employeeId"
                      value={formData.employeeId}
                      onChange={handleInputChange}
                      placeholder="Enter employee ID"
                      disabled={isSubmitting}
                    />
                    {formErrors.employeeId && <div className="invalid-feedback">{formErrors.employeeId}</div>}
                  </div>
                  <div className="col-md-6">
                    <AppSelect
                      name="departementId"
                      label="Department"
                      required
                      value={formData.departementId}
                      onChange={handleSelectChange}
                      options={departments}
                      disabled={isSubmitting}
                      error={formErrors.departementId}
                      placeholder="Select department"
                    />
                  </div>
                  <div className="col-md-6">
                    <AppSelect
                      name="jobTitleId"
                      label="Job Title"
                      required
                      value={formData.jobTitleId}
                      onChange={handleSelectChange}
                      options={jobTitles}
                      disabled={isSubmitting}
                      error={formErrors.jobTitleId}
                      placeholder="Select job title"
                    />
                  </div>
                  <div className="col-md-6">
                    <AppSelect
                      name="branchIds"
                      label="Branch"
                      value={formData.branchIds}
                      onChange={handleSelectChange}
                      options={branches}
                      multiple
                      disabled={isSubmitting}
                      placeholder="Select branch"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Business Location</label>
                    <input
                      type="text"
                      className="form-control"
                      name="businessLocation"
                      value={formData.businessLocation}
                      onChange={handleInputChange}
                      placeholder="Enter business location"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="inv-section-label">Address</div>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-control"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Enter address"
                      rows={2}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Country</label>
                    <input
                      type="text"
                      className="form-control"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      className="form-control"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-control"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Postal Code</label>
                    <input
                      type="text"
                      className="form-control"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="inv-section-label">Emergency Contact</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Emergency Contact</label>
                    <input
                      type="text"
                      className="form-control"
                      name="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={handleInputChange}
                      placeholder="Enter emergency contact"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Emergency Address</label>
                    <input
                      type="text"
                      className="form-control"
                      name="emergencyAddress"
                      value={formData.emergencyAddress}
                      onChange={handleInputChange}
                      placeholder="Enter emergency address"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="inv-section-label">Photo &amp; Signature</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Profile Picture</label>
                    <div className="d-flex align-items-center gap-3">
                      {profilePreview && (
                        <img src={profilePreview} alt="Profile preview" className="avatar-upload-preview" />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={handleProfilePictureChange}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Signature</label>
                    <div className="d-flex align-items-center gap-3">
                      {signaturePreview && (
                        <img src={signaturePreview} alt="Signature preview" className="avatar-upload-preview" style={{ borderRadius: 8 }} />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={handleSignatureChange}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  {isSubmitting ? 'Saving…' : (
                    <span style={{ color: '#9aa5b1' }}>
                      <span className="inv-required-star">*</span> Required fields
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleModalClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-inv-save"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14, borderWidth: 2 }} />
                      Saving…
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      {isEditMode ? 'Update' : 'Save'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
    </>
  );
};

CreateUserForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  isEditMode: PropTypes.bool,
};

export default CreateUserForm;
